import React, { useState, useEffect } from 'react';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import { i18nChangeLanguage } from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css';
import { adminFileApi } from '../api';

// wangEditor 默认 UI 语言切换为中文
i18nChangeLanguage('zh-CN');

// 精简工具栏（移动端 / 邮件模板等空间受限场景）
const SIMPLE_TOOLBAR_KEYS = [
  'headerSelect',
  'bold', 'italic', 'underline', 'through',
  '|',
  'color', 'bgColor',
  '|',
  'bulletedList', 'numberedList',
  '|',
  'insertLink', 'uploadImage',
  '|',
  'clearStyle',
];

// 完整工具栏（桌面端商品详情 / 营销邮件正文）
const FULL_TOOLBAR_KEYS = [
  'headerSelect',
  '|',
  'bold', 'italic', 'underline', 'through', 'sup', 'sub',
  '|',
  'color', 'bgColor', 'fontSize', 'fontFamily', 'lineHeight',
  '|',
  'bulletedList', 'numberedList',
  'justifyLeft', 'justifyCenter', 'justifyRight',
  '|',
  'insertLink', 'uploadImage', 'insertTable',
  'divider', 'blockquote', 'code',
  '|',
  'undo', 'redo',
  '|',
  'clearStyle',
];

/**
 * 通用富文本编辑器（后台用）。
 * 受控组件：value/onChange 都是 HTML 字符串。
 * 图片上传对接管理端 /api/admin/files。
 *
 * 内容同步由 @wangeditor/editor-for-react 的 Editor 组件内建的 value 属性负责，
 * 不再自己维护 useRef+setHtml 的旁路——防止多个 setHtml 竞态导致初值不回显。
 *
 * @param {string}   value        HTML 字符串
 * @param {function} onChange     HTML 变更回调
 * @param {boolean}  simple       精简工具栏 + 更矮的编辑区
 * @param {number}   height       编辑区高度（不传则 simple=260 / 桌面=400）
 * @param {string}   placeholder  占位符
 */
export default function RichTextEditor({
  value = '',
  onChange,
  simple = false,
  height,
  placeholder = '请输入内容，可使用 {{customerName}} 等模板变量...',
}) {
  const [editor, setEditor] = useState(null);

  const toolbarConfig = {
    toolbarKeys: simple ? SIMPLE_TOOLBAR_KEYS : FULL_TOOLBAR_KEYS,
  };

  const editorConfig = {
    placeholder,
    MENU_CONF: {
      uploadImage: {
        // 自定义上传：走后台 /api/admin/files，字段名 file
        async customUpload(file, insertFn) {
          try {
            const attached = await adminFileApi.upload(file);
            const url = attached && attached.url;
            if (url) {
              insertFn(url, attached.originalFilename || '', url);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('富文本图片上传失败:', e);
          }
        },
        allowedFileTypes: ['image/*'],
        maxFileSize: 10 * 1024 * 1024, // 10MB
      },
    },
  };

  // 组件卸载时销毁编辑器实例（wangEditor 官方建议）
  useEffect(() => {
    return () => {
      if (editor == null) return;
      editor.destroy();
      setEditor(null);
    };
  }, [editor]);

  const handleChange = (ed) => {
    if (onChange) onChange(ed.getHtml());
  };

  return (
    <div style={{ border: '1px solid #d9d9d9', borderRadius: 4, zIndex: 100 }}>
      <Toolbar
        editor={editor}
        defaultConfig={toolbarConfig}
        mode="default"
        style={{ borderBottom: '1px solid #e8e8e8' }}
      />
      <Editor
        defaultConfig={editorConfig}
        value={value || ''}
        defaultHtml={value || ''}
        onCreated={setEditor}
        onChange={handleChange}
        mode="default"
        style={{ height: height || (simple ? 260 : 400), overflowY: 'hidden' }}
      />
    </div>
  );
}
